import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useVoiceInput — thin hook around browser SpeechRecognition.
 *
 * Returns { supported, listening, error, start, stop, transcript, reset }.
 * Never auto-submits — caller decides what to do with `transcript`.
 *
 * Callers typically:
 *   const v = useVoiceInput({ onFinal: (t) => setValue(prev => prev + ' ' + t) });
 *   <button onClick={v.listening ? v.stop : v.start}>...</button>
 */
export function useVoiceInput({ onFinal, onInterim, lang = 'en-US' } = {}) {
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState('');

  // Detect support at mount
  useEffect(() => {
    const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    setSupported(!!SR);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      setSupported(false);
      setError('unsupported');
      return;
    }
    // Ensure any prior instance is stopped
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }

    const rec = new SR();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = true;

    let finalText = '';
    rec.onresult = (event) => {
      let interim = '';
      let finalNew = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        const s = r[0].transcript;
        if (r.isFinal) finalNew += s;
        else interim += s;
      }
      if (finalNew) {
        finalText += finalNew;
        setTranscript(prev => (prev + ' ' + finalNew).trim());
        onFinal?.(finalNew.trim());
      }
      if (interim && onInterim) onInterim(interim.trim());
    };
    rec.onerror = (e) => {
      const code = e?.error || 'unknown';
      // 'no-speech' just means silence — not really an error
      if (code === 'no-speech') { return; }
      setError(code);
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        setError('permission-denied');
      }
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
    };
    try {
      rec.start();
      recognitionRef.current = rec;
      setListening(true);
    } catch (e) {
      setError('start-failed');
      setListening(false);
    }
  }, [lang, onFinal, onInterim]);

  const stop = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
    setListening(false);
  }, []);

  const reset = useCallback(() => {
    setTranscript('');
    setError(null);
  }, []);

  useEffect(() => () => { try { recognitionRef.current?.stop(); } catch { /* noop */ } }, []);

  return { supported, listening, error, start, stop, transcript, reset };
}
